import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { useDispatch } from 'react-redux';
import { SubmitErrorHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StepButtons } from '../../ui/step/components/step.buttons';
import commonStyles from '../common.module.scss';
import { useAnalytics } from '../../../hooks/analytics';
import { extendFbPayload, extendFormStore, setPageContext } from '../../../../store/reducers/formStore';
import { useFormData, useFormStore } from '../../../../store/selectors/formStore';
import { Row } from '../common';
import { useSelection } from '../../../../store/reducers/selection';
import { Col, Rows } from '../../../landing.common';
import { postMessage, PostMessageType } from '../../../../helpers/whitelable';
import { UNEMPLOYMENT_REASONS } from '../../../../constants/sources';
import { scrollToForm } from '../../../../helpers';
import { Categories, EventType } from '../../../../helpers/analytics';
import { FORM_FIELDS, ORGANIZATION_CHOICE_TYPE, PAGE, PAGE_CONTEXT } from '../../../../constants';
import { Layout } from '../../ui/step';
import {
    AnotherUnemploymentReasonField,
    EmploymentTypeField,
    JobTitleField,
    JobTypeField,
    UnEmploymentReasonField,
    WorkPhoneField,
} from '../../../../helpers/form.new/fields/work';
import { StepContext } from '../../creator/step.context';
import { useAbAdditionalQuestions } from '../../../../helpers/experiments/additional.questions';
import { useFormOnBlur } from '../../hooks/formHooks';
import { useEsiaReturnAnalytic } from '../../hooks/esiaReturnAnalytic';
import { LastExperienceFields, SalaryField, WorkPlaceFields, OrganizationNameField } from './components';
import { namesOfOrganizationFields, WPViews } from './constants';
import { schema } from './schema';
import { needed as fieldNeeded } from './dependencies';
import { AdditionalQuestions } from './components/additional.questions';

export const WorkStepView: FC = React.memo(() => {
    const { page } = useSelection();
    const { pushEvent } = useAnalytics();
    const { nextStep, prevStep, analyticStepText } = useContext(StepContext);
    const dispatch = useDispatch();
    const { withoutAnalytics, headline } = useFormStore();
    const formData = useFormData();
    const [organizationStatus, setOrganizationStatus] = useState<string | null>(null);

    const form = useForm({
        resolver: yupResolver(schema()),
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const {
        control,
        formState: { errors, dirtyFields: _, isSubmitting, isValidating },
        reset,
        setValue,
        handleSubmit,
        watch,
        clearErrors,
        getValues,
    } = form;

    const additionalQuestionsVariant = useAbAdditionalQuestions();
    const isShowAdditionalQuestions = additionalQuestionsVariant === '1';

    const [employmentType, unemploymentReason] = watch([FORM_FIELDS.EMPLOYMENT_TYPE, FORM_FIELDS.UNEMPLOYMENT_REASON]);

    useEsiaReturnAnalytic('Шаг 3|Место работы');
    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    const areAllWorkPlaceFieldsFilled = useCallback(
        () => namesOfOrganizationFields.every((fieldName) => formData[fieldName]),
        [formData],
    );
    const areAllWorkPlaceFieldsEmpty = useCallback(
        () => namesOfOrganizationFields.every((fieldName) => !formData[fieldName]),
        [formData],
    );

    const getFirstWorkPlaceViewValue = useCallback(() => {
        if (areAllWorkPlaceFieldsEmpty()) {
            return WPViews.wpHidden;
        }

        if (areAllWorkPlaceFieldsFilled()) {
            return WPViews.wpShort;
        }

        return WPViews.wpVisible;
    }, [areAllWorkPlaceFieldsEmpty, areAllWorkPlaceFieldsFilled]);

    const [workPlaceView, setWorkPlaceView] = useState<WPViews>(getFirstWorkPlaceViewValue());

    useEffect(() => {
        setWorkPlaceView(getFirstWorkPlaceViewValue());
    }, [employmentType, getFirstWorkPlaceViewValue]);

    useEffect(() => {
        scrollToForm();
        dispatch(setPageContext(PAGE_CONTEXT.STEP_3));
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Просмотр экрана|Шаг 3|Место работы',
        });

        if (page === PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_work_load',
            });
        }
    }, []);

    const openWorkPlaceEditor = () => setWorkPlaceView(WPViews.wpVisible);
    const closeWorkPlaceEditor = () => setWorkPlaceView(WPViews.wpHidden);
    const minimizeWorkPlaceEditor = () => setWorkPlaceView(WPViews.wpShort);

    const finishEditOrganization = useCallback(() => {
        if (areAllWorkPlaceFieldsFilled()) {
            minimizeWorkPlaceEditor();
        }
    }, [areAllWorkPlaceFieldsFilled]);

    const onOrganizationFocus: React.FocusEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            const { target } = event;
            /** Некоторые элементы, например Select, отдают div вместо конечного input */
            const inputElem =
                (target.hasAttribute('name') && target.hasAttribute('value') && target) ||
                target.querySelector('[name][value]');
            const fieldName =
                inputElem && (inputElem.getAttribute('name') as (typeof namesOfOrganizationFields)[number]);
            if (!fieldName || fieldName === FORM_FIELDS.ORGANIZATION_NAME) {
                return;
            }
            if (!namesOfOrganizationFields.includes(fieldName)) {
                finishEditOrganization();
            } else {
                openWorkPlaceEditor();
            }
        },
        [finishEditOrganization],
    );

    const clearFields = useCallback(
        (fields: ReadonlyArray<string>) => {
            fields.forEach((fieldName) => setValue(fieldName, ''));
            closeWorkPlaceEditor();
        },
        [setValue],
    );

    const onOrganizationClear = useCallback(() => {
        clearFields(namesOfOrganizationFields);
        closeWorkPlaceEditor();
    }, []);

    const onOrganizationBlur: React.ComponentProps<typeof OrganizationNameField>['onBlur'] = useCallback(
        (event, onBlur, option?: Dadata.OrganizationSuggestItem) => {
            const { target } = event;

            if (option) {
                dispatch(extendFormStore({ organizationChoiceType: ORGANIZATION_CHOICE_TYPE.SUGGEST }));
                setOrganizationStatus(option.data.state.status);
            } else {
                dispatch(extendFormStore({ organizationChoiceType: ORGANIZATION_CHOICE_TYPE.BY_YOURSELF }));
            }

            if (!areAllWorkPlaceFieldsFilled) {
                if (target.value) {
                    openWorkPlaceEditor();
                } else {
                    closeWorkPlaceEditor();
                }
            }

            finishEditOrganization();
            onBlur(event);
        },
        [areAllWorkPlaceFieldsFilled, dispatch, finishEditOrganization],
    );

    const onOrganizationChange = useCallback(() => {
        if (areAllWorkPlaceFieldsFilled()) {
            clearFields(namesOfOrganizationFields.filter((fieldName) => fieldName !== FORM_FIELDS.ORGANIZATION_NAME));
            closeWorkPlaceEditor();
        }
    }, [areAllWorkPlaceFieldsFilled, clearFields]);

    const isUnemploymentOtherReason = Boolean(
        fieldNeeded(FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON, employmentType) &&
            unemploymentReason === UNEMPLOYMENT_REASONS.OTHER,
    );

    const goNext = useCallback(() => {
        dispatch(extendFbPayload({ work_organization_status: organizationStatus || undefined }));

        nextStep();
    }, [organizationStatus, dispatch, nextStep]);

    const onSubmitError: SubmitErrorHandler<Record<string, unknown>> = useCallback(
        (e) => {
            const errorKey = Object.keys(e)[0];
            const errorValue = getValues(errorKey);

            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Невалидная анкета',
                eventLabel: `${analyticStepText}|${errorKey}|Ручной проход|${errorValue}`,
            });
        },
        [analyticStepText],
    );

    const formOnBlur = useFormOnBlur(form);

    const formOnChange: React.FormEventHandler<HTMLFormElement> = (event) => {
        const { name: fieldName } = event.target as HTMLInputElement;
        if (fieldName && fieldName in errors) {
            clearErrors(fieldName);
        }
    };

    const goBack = useCallback(() => {
        if (!withoutAnalytics) {
            pushEvent({
                event: EventType.mainEvent,
                eventCategory: [PAGE.CARDS_RESTORE].includes(page as any) ? Categories.CreditCard : Categories.Loan,
                eventAction: 'Шаг 3|Место работы|Шаг Назад',
            });
        }
        prevStep();
    }, [prevStep, page, withoutAnalytics, pushEvent]);

    return (
        <Layout
            headline={headline}
            onSubmit={handleSubmit(goNext, onSubmitError)}
            onBlur={formOnBlur}
            onChange={formOnChange}
            navigation={true}
            locked={isSubmitting}
            greenMessageIcon="check"
            greenMessage="Чем больше полей вы заполните, тем больше предложений банков будет доступно"
            step={3}>
            <Rows>
                <Row>
                    <Col>
                        <Heading level={4}>Место работы</Heading>
                    </Col>
                </Row>
                <Row onFocus={onOrganizationFocus}>
                    <Col md-4>
                        <EmploymentTypeField control={control} error={errors[FORM_FIELDS.EMPLOYMENT_TYPE]?.message} />
                    </Col>

                    {fieldNeeded(FORM_FIELDS.ORGANIZATION_NAME, employmentType) && (
                        <Col md-4>
                            <OrganizationNameField
                                form={form}
                                onChange={onOrganizationChange}
                                onBlur={onOrganizationBlur}
                                onClear={onOrganizationClear}
                                onManual={openWorkPlaceEditor}
                            />
                        </Col>
                    )}

                    {fieldNeeded(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH, employmentType) && (
                        <Col md-4>
                            <LastExperienceFields form={form} />
                        </Col>
                    )}
                </Row>
                <WorkPlaceFields
                    form={form}
                    workPlaceView={workPlaceView}
                    onOpenEditor={openWorkPlaceEditor}
                    onFocus={onOrganizationFocus}
                />
                <Row onFocus={onOrganizationFocus}>
                    {fieldNeeded(FORM_FIELDS.WORK_PHONE, employmentType) && (
                        <Col md-4>
                            <WorkPhoneField control={control} error={errors[FORM_FIELDS.WORK_PHONE]?.message} />
                        </Col>
                    )}

                    {fieldNeeded(FORM_FIELDS.UNEMPLOYMENT_REASON, employmentType) && (
                        <Col md-4>
                            <UnEmploymentReasonField
                                control={control}
                                error={errors[FORM_FIELDS.UNEMPLOYMENT_REASON]?.message}
                            />
                        </Col>
                    )}

                    {isUnemploymentOtherReason && (
                        <Col md-4>
                            <AnotherUnemploymentReasonField
                                control={control}
                                error={errors[FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON]?.message}
                            />
                        </Col>
                    )}

                    {fieldNeeded(FORM_FIELDS.JOB_TITLE, employmentType) && (
                        <Col md-4>
                            <JobTitleField control={control} error={errors[FORM_FIELDS.JOB_TITLE]?.message} />
                        </Col>
                    )}

                    {fieldNeeded(FORM_FIELDS.JOB_TYPE, employmentType) && (
                        <Col md-4>
                            <JobTypeField control={control} error={errors[FORM_FIELDS.JOB_TYPE]?.message} />
                        </Col>
                    )}

                    <Col md-4>
                        <SalaryField form={form} />
                    </Col>
                </Row>
                {isShowAdditionalQuestions && <AdditionalQuestions form={form} />}
            </Rows>
            <StepButtons
                continueButtonDisabled={isValidating}
                onReturnButtonClick={goBack}
                className={commonStyles['step-buttons-wrapper']}
            />
        </Layout>
    );
});

WorkStepView.displayName = 'WorkStepView';
